/**
 * URL-backed query state + data loading for the Roster Manager.
 *
 * Mirrors `pages/team-admin/use-team-users.ts`: the whole query (search, cohort
 * filter, sort, page, page size) lives in the address bar, so a refresh, a
 * bookmark and a shared link all restore the same view, and the back button
 * steps through filter changes.
 *
 * Two hooks, because the page needs two independent resources:
 *   `useRoster()`        → `GET /players/v1`
 *   `useCohortCatalog()` → `GET /cohort-values/v1` (labels + dropdown options)
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useApiClient } from '../../app/providers';
import { ApiError } from '../../lib/api-error';
import type { CohortValue, Player, PlayerPage } from '../../lib/api-types';
import { PAGE_SIZE_OPTIONS } from '../../lib/constants';
import {
  enumParam,
  intChoiceParam,
  intParam,
  readUrlState,
  textParam,
  writeUrlState,
  URL_PARAM_KEYS,
} from '../../lib/url-state';

/** Whitelisted `sort_by` values — must match `PLAYER_SORT_FIELDS` in services/roster. */
export const PLAYER_SORT_FIELDS = [
  'jersey_number',
  'last_name',
  'first_name',
  'cohort',
  'position',
  'created_at',
] as const;
export type PlayerSortField = (typeof PLAYER_SORT_FIELDS)[number];

/** A roster is usually 30–80 players, so 25 rows is one or two pages. */
export const ROSTER_DEFAULT_PAGE_SIZE = 25;
export const ROSTER_DEFAULT_SORT: PlayerSortField = 'last_name';
/** One request is enough for any realistic catalog (service caps at 500). */
export const COHORT_CATALOG_LIMIT = 500;

export interface RosterQueryState {
  search: string;
  /** Cohort code, or `all`. */
  cohort: string;
  sortBy: PlayerSortField;
  sortOrder: 'asc' | 'desc';
  page: number;
  pageSize: number;
}

/** Declarative screen state (shared url-state kit): parse is total, defaults stay off the URL. */
const ROSTER_URL_STATE = {
  search: textParam(URL_PARAM_KEYS.search),
  cohort: textParam(URL_PARAM_KEYS.cohort, 'all'),
  sortBy: enumParam(URL_PARAM_KEYS.sortBy, PLAYER_SORT_FIELDS, ROSTER_DEFAULT_SORT),
  sortOrder: enumParam(URL_PARAM_KEYS.sortOrder, ['asc', 'desc'] as const, 'asc'),
  page: intParam(URL_PARAM_KEYS.page, { fallback: 1, min: 1 }),
  pageSize: intChoiceParam(URL_PARAM_KEYS.pageSize, PAGE_SIZE_OPTIONS, ROSTER_DEFAULT_PAGE_SIZE),
};

function readQuery(params: URLSearchParams): RosterQueryState {
  return readUrlState(ROSTER_URL_STATE, params);
}

function writeQuery(next: RosterQueryState, base: URLSearchParams): URLSearchParams {
  return writeUrlState(ROSTER_URL_STATE, next, base);
}

export interface RosterController {
  query: RosterQueryState;
  /** Merge a partial query. Anything but an explicit page change resets to page 1. */
  setQuery: (patch: Partial<RosterQueryState>) => void;
  /** True when no search/filter is applied — distinguishes first run from no matches. */
  isUnfiltered: boolean;
  players: readonly Player[];
  total: number;
  isLoading: boolean;
  error: string | null;
  reload: () => void;
}

export function useRoster(): RosterController {
  const apiClient = useApiClient();
  const [params, setParams] = useSearchParams();
  const [page, setPage] = useState<PlayerPage | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  const query = useMemo(() => readQuery(params), [params]);

  const setQuery = useCallback(
    (patch: Partial<RosterQueryState>) => {
      const next = { ...readQuery(params), ...patch };
      if (patch.page === undefined) next.page = 1;
      setParams(writeQuery(next, params), { replace: true });
    },
    [params, setParams]
  );

  const reload = useCallback(() => setReloadToken((token) => token + 1), []);

  useEffect(() => {
    const controller = new AbortController();
    setIsLoading(true);
    setError(null);

    apiClient
      .listPlayers(
        {
          q: query.search === '' ? undefined : query.search,
          cohort: query.cohort === 'all' ? undefined : query.cohort,
          sort_by: query.sortBy,
          sort_order: query.sortOrder,
          page: query.page,
          limit: query.pageSize,
        },
        controller.signal
      )
      .then((result) => {
        if (!controller.signal.aborted) setPage(result);
      })
      .catch((caught: unknown) => {
        if (controller.signal.aborted) return;
        setError(
          ApiError.isApiError(caught)
            ? caught.message
            : 'The roster could not be loaded. Please try again.'
        );
      })
      .finally(() => {
        if (!controller.signal.aborted) setIsLoading(false);
      });

    return () => controller.abort();
  }, [apiClient, query, reloadToken]);

  return {
    query,
    setQuery,
    isUnfiltered: query.search === '' && query.cohort === 'all',
    players: page?.items ?? [],
    total: page?.total ?? 0,
    isLoading,
    error,
    reload,
  };
}

export interface PlayerSelection {
  players: readonly Player[];
  ids: ReadonlySet<string>;
  toggle: (player: Player, isSelected: boolean) => void;
  /** Select/clear every row currently on screen, leaving other pages alone. */
  togglePage: (page: readonly Player[], isSelected: boolean) => void;
  clear: () => void;
}

/** Multi-select state for the bulk delete action; survives paging. */
export function usePlayerSelection(): PlayerSelection {
  const [players, setPlayers] = useState<readonly Player[]>([]);

  const toggle = useCallback((player: Player, isSelected: boolean) => {
    setPlayers((current) => {
      const without = current.filter((entry) => entry.player_id !== player.player_id);
      return isSelected ? [...without, player] : without;
    });
  }, []);

  const togglePage = useCallback((page: readonly Player[], isSelected: boolean) => {
    const pageIds = new Set(page.map((player) => player.player_id));
    setPlayers((current) => {
      const others = current.filter((entry) => !pageIds.has(entry.player_id));
      return isSelected ? [...others, ...page] : others;
    });
  }, []);

  return {
    players,
    ids: useMemo(() => new Set(players.map((player) => player.player_id)), [players]),
    toggle,
    togglePage,
    clear: useCallback(() => setPlayers([]), []),
  };
}

/** Catalog order: by category, then the tenant's `sort_order`, then label. */
function byCatalogOrder(left: CohortValue, right: CohortValue): number {
  if (left.category !== right.category) return left.category.localeCompare(right.category);
  if (left.sort_order !== right.sort_order) return left.sort_order - right.sort_order;
  return left.label.localeCompare(right.label);
}

export interface CohortCatalog {
  /** Everything in the catalog, deprecated rows included (labels must resolve). */
  values: readonly CohortValue[];
  /** What a NEW assignment may use — deprecated rows are filtered out. */
  assignable: readonly CohortValue[];
  isLoading: boolean;
  error: string | null;
  reload: () => void;
  /** Splice a just-created value in so the dropdown updates without a refetch. */
  register: (value: CohortValue) => void;
}

export function useCohortCatalog(): CohortCatalog {
  const apiClient = useApiClient();
  const [values, setValues] = useState<readonly CohortValue[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  const reload = useCallback(() => setReloadToken((token) => token + 1), []);

  const register = useCallback((value: CohortValue) => {
    setValues((current) =>
      [...current.filter((entry) => entry.id !== value.id), value].sort(byCatalogOrder)
    );
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    setIsLoading(true);
    setError(null);

    apiClient
      .listCohortValues(
        { include_deprecated: true, limit: COHORT_CATALOG_LIMIT },
        controller.signal
      )
      .then((result) => {
        if (!controller.signal.aborted) setValues([...result.items].sort(byCatalogOrder));
      })
      .catch((caught: unknown) => {
        if (controller.signal.aborted) return;
        setError(
          ApiError.isApiError(caught)
            ? caught.message
            : 'The cohort catalog could not be loaded. Please try again.'
        );
      })
      .finally(() => {
        if (!controller.signal.aborted) setIsLoading(false);
      });

    return () => controller.abort();
  }, [apiClient, reloadToken]);

  return {
    values,
    assignable: useMemo(() => values.filter((value) => !value.is_deprecated), [values]),
    isLoading,
    error,
    reload,
    register,
  };
}
